import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  ChartPieIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  Bars3Icon,
  XMarkIcon,
  BoltSlashIcon,
  CpuChipIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/utils/cn';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { QuickActions } from '@/components/ui/QuickActions';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useResponsiveNavigation } from '@/hooks/useResponsiveNavigation';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const Layout: React.FC = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const {
    sidebarOpen,
    mobileMenuOpen,
    toggleSidebar,
    toggleMobileMenu,
    closeMobileMenu,
  } = useResponsiveNavigation();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const navGroups: NavGroup[] = [
    {
      label: 'Overview',
      items: [
        {
          path: '/',
          label: 'Dashboard',
          icon: ChartBarIcon,
          group: 'overview',
        },
        {
          path: '/analytics',
          label: 'Analytics',
          icon: ChartPieIcon,
          group: 'overview',
        },
        {
          path: '/cost-monitoring',
          label: 'Cost Monitoring',
          icon: CurrencyDollarIcon,
          group: 'overview',
        },
      ],
    },
    {
      label: 'Management',
      items: [
        {
          path: '/users',
          label: 'Users',
          icon: UsersIcon,
          group: 'management',
        },
        {
          path: '/organizations',
          label: 'Organizations',
          icon: BuildingOfficeIcon,
          group: 'management',
        },
        {
          path: '/jobs',
          label: 'Jobs',
          icon: BoltSlashIcon,
          group: 'management',
        },
      ],
    },
    {
      label: 'System',
      items: [
        {
          path: '/models',
          label: 'Models',
          icon: CpuChipIcon,
          group: 'system',
        },
        {
          path: '/audit-logs',
          label: 'Audit Logs',
          icon: DocumentTextIcon,
          group: 'system',
        },
        {
          path: '/settings',
          label: 'Settings',
          icon: Cog6ToothIcon,
          group: 'system',
        },
      ],
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const breadcrumbs = useBreadcrumbs();

  return (
    <div className="min-h-screen bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 flex font-sans text-slate-300">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar with Glassmorphism */}
      <aside
        id="navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-slate-900/50 backdrop-blur-xl border-r border-white/5 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 shadow-2xl rounded-r-2xl lg:rounded-none',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          !sidebarOpen && 'lg:w-20'
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-20 px-6 border-b border-white/5 bg-slate-900/20">
          <div
            className={cn(
              'flex items-center space-x-3 transition-all duration-300 overflow-hidden',
              !sidebarOpen && 'lg:w-8 lg:opacity-0 lg:absolute lg:-left-20' // Hides text nicely
            )}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
              <span className="text-white font-bold text-sm tracking-wider">
                SM
              </span>
            </div>
            <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 shrink-0 whitespace-nowrap">
              Admin Portal
            </h1>
          </div>

          {/* Small emblem when collapsed */}
          {!sidebarOpen && (
            <div className="hidden lg:flex w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg shrink-0 mx-auto transition-all">
              <span className="text-white font-bold text-sm">SM</span>
            </div>
          )}

          {/* Desktop toggle */}
          {/* Mobile close */}
          <button
            onClick={closeMobileMenu}
            className="lg:hidden flex items-center justify-center w-8 h-8 text-slate-400 hover:text-white rounded-md transition-colors"
            aria-label="Close menu"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-8 overflow-y-auto no-scrollbar">
          {navGroups.map((group) => (
            <div key={group.label} className="space-y-3">
              <h3
                className={cn(
                  'px-3 text-xs font-semibold text-slate-500 uppercase tracking-widest transition-opacity duration-200',
                  !sidebarOpen && 'lg:opacity-0 lg:h-0 lg:overflow-hidden'
                )}
              >
                {group.label}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 relative overflow-hidden',
                        active
                          ? 'text-white shadow-lg'
                          : 'text-slate-400 hover:text-white'
                      )}
                      title={!sidebarOpen ? item.label : undefined}
                    >
                      {active && (
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 opacity-100" />
                      )}

                      {/* Active indicator line */}
                      {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1/2 bg-blue-500 rounded-r-full" />
                      )}

                      <Icon
                        className={cn(
                          'flex-shrink-0 w-5 h-5 transition-all duration-300 relative z-10',
                          active
                            ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]'
                            : 'group-hover:text-slate-200'
                        )}
                      />
                      <span
                        className={cn(
                          'ml-3 transition-opacity duration-200 relative z-10 whitespace-nowrap',
                          !sidebarOpen &&
                            'lg:opacity-0 lg:w-0 lg:overflow-hidden lg:ml-0'
                        )}
                      >
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 m-4 rounded-2xl bg-slate-800/30 border border-white/5 backdrop-blur-md transition-all">
          <div
            className={cn(
              'flex items-center space-x-3 transition-opacity duration-200',
              !sidebarOpen && 'justify-center space-x-0'
            )}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-slate-700 to-slate-600 border border-white/10 flex items-center justify-center shrink-0">
              <span className="text-white text-sm font-semibold">
                {user?.email?.[0].toUpperCase() || 'A'}
              </span>
            </div>

            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-blue-400 font-medium tracking-wide uppercase mt-0.5">
                  Admin
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Decorative background glow */}
        <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] opacity-50 mix-blend-screen pointer-events-none" />

        {/* Header */}
        <header className="h-20 flex items-center justify-between px-6 lg:px-10 bg-slate-900/30 backdrop-blur-md border-b border-white/5 sticky top-0 z-30">
          {/* Left side */}
          <div className="flex items-center space-x-6">
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex items-center justify-center w-10 h-10 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
              aria-label="Toggle sidebar"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden flex items-center justify-center w-10 h-10 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
              aria-label="Open menu"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>

            {/* Breadcrumbs */}
            <div className="hidden sm:block">
              <Breadcrumb items={breadcrumbs} className="text-slate-400" />
            </div>
          </div>

          {/* Right side - Quick Actions */}
          <div className="flex items-center gap-4">
            <QuickActions userEmail={user?.email} onSignOut={handleSignOut} />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto no-scrollbar">
          <div className="p-6 lg:p-10 max-w-[1600px] mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
